import React, { useEffect, useState } from 'react';
import './ListaTratamientos.css';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const ListaTratamientos = ({ refresh }) => {
  const [tratamientos, setTratamientos] = useState([]);
  const [open, setOpen] = useState(false);
  const [tratamientoToEdit, setTratamientoToEdit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Cargar tratamientos
  useEffect(() => {
    const fetchTratamientos = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tratamientos`);
        const data = await response.json();
        const activos = data.filter(tratamiento => tratamiento.activo === 1);
        setTratamientos(activos);
      } catch (error) {
        console.error("Error al obtener los tratamientos:", error);
      }
    };

    fetchTratamientos();
  }, [refresh]);

  // Abrir y cerrar modal de edición
  const handleClickOpen = (tratamiento) => {
    setTratamientoToEdit(tratamiento);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setTratamientoToEdit(null);
  };

  // Manejar cambios en campos de texto
  const handleChange = (e) => {
    const { name, value } = e.target;
    setTratamientoToEdit({
      ...tratamientoToEdit,
      [name]: value,
    });
  };

  // Guardar cambios en tratamiento
  const saveChanges = async () => {
    if (tratamientoToEdit) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tratamientos/${tratamientoToEdit.id_tratamiento}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(tratamientoToEdit),
        });
        if (response.ok) {
          setTratamientos(prev =>
            prev.map(t =>
              t.id_tratamiento === tratamientoToEdit.id_tratamiento ? { ...t, ...tratamientoToEdit } : t
            )
          );
          handleClose();
        }
      } catch (error) {
        console.error("Error al guardar los cambios:", error);
      }
    }
  };

  // 🗑️ Eliminar tratamiento (cambiar estado a inactivo)
  const eliminarTratamiento = async (id_tratamiento) => {
    const confirmacion = window.confirm("¿Seguro que deseas desactivar este tratamiento?");
    if (!confirmacion) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cambiarestadotratamiento/${id_tratamiento}`, {
        method: 'PUT',
      });

      if (response.ok) {
        alert("Tratamiento desactivado correctamente.");
        setTratamientos(prev => prev.filter(t => t.id_tratamiento !== id_tratamiento));
      } else {
        alert("Error al desactivar el tratamiento.");
      }
    } catch (error) {
      console.error("Error al cambiar estado del tratamiento:", error);
      alert("No se pudo desactivar el tratamiento.");
    }
  };

  // Filtrar y ordenar tratamientos
  const tratamientosFiltrados = tratamientos.filter(t =>
    t.nombre_tratamiento.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const ordenarPor = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const tratamientosOrdenados = [...tratamientosFiltrados].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let valA = a[sortConfig.key];
    let valB = b[sortConfig.key];
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();
    if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="tabla-tratamientos-container">
      <TextField
        label="Buscar tratamiento"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <p><strong>Total de tratamientos:</strong> {tratamientosFiltrados.length}</p>

      <table id="tabla-tratamientos">
        <thead>
          <tr>
            <th onClick={() => ordenarPor('nombre_tratamiento')} style={{ cursor: 'pointer' }}>Nombre</th>
            <th onClick={() => ordenarPor('duracion_tratamiento')} style={{ cursor: 'pointer' }}>Duración</th>
            <th onClick={() => ordenarPor('precio_tratamiento')} style={{ cursor: 'pointer' }}>Precio (€)</th>
            <th onClick={() => ordenarPor('marca')} style={{ cursor: 'pointer' }}>Marca</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {tratamientosOrdenados.map(tratamiento => (
            <tr key={tratamiento.id_tratamiento} className="tratamiento-row">
              <td onDoubleClick={() => handleClickOpen(tratamiento)}>{tratamiento.nombre_tratamiento}</td>
              <td onDoubleClick={() => handleClickOpen(tratamiento)}>{tratamiento.duracion_tratamiento} min</td>
              <td onDoubleClick={() => handleClickOpen(tratamiento)}>€{tratamiento.precio_tratamiento}</td>
              <td onDoubleClick={() => handleClickOpen(tratamiento)}>{tratamiento.marca}</td>
              <td style={{ textAlign: 'center' }}>
                <IconButton color="error" onClick={() => eliminarTratamiento(tratamiento.id_tratamiento)}>
                  <DeleteIcon />
                </IconButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Editar Tratamiento</DialogTitle>
        <DialogContent>
          {tratamientoToEdit && (
            <div>
              <TextField
                label="Nombre del Tratamiento"
                variant="outlined"
                fullWidth
                margin="normal"
                name="nombre_tratamiento"
                value={tratamientoToEdit.nombre_tratamiento}
                onChange={handleChange}
              />
              <TextField
                label="Duración (min)"
                variant="outlined"
                fullWidth
                margin="normal"
                name="duracion_tratamiento"
                value={tratamientoToEdit.duracion_tratamiento}
                onChange={handleChange}
                type="number"
              />
              <TextField
                label="Precio (€)"
                variant="outlined"
                fullWidth
                margin="normal"
                name="precio_tratamiento"
                value={tratamientoToEdit.precio_tratamiento}
                onChange={handleChange}
                type="number"
              />
              <TextField
                label="Marca"
                variant="outlined"
                fullWidth
                margin="normal"
                name="marca"
                value={tratamientoToEdit.marca}
                onChange={handleChange}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">Cancelar</Button>
          <Button onClick={saveChanges} color="primary">Guardar Cambios</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ListaTratamientos;
